import {
  quotesGrid,
  postQuoteTypes,
  dropDown,
  targetQuoteTypes,
  updateQuoteTypes,
  fileDocumentTypes,
} from './interfaces'
import { DateFormatter } from '../data/helper'
import axios from 'axios'

const apiURL = process.env.REACT_APP_API_URL

const dateFormatter = new DateFormatter()

export const getQuotes = async (
  showRejected: boolean,
  month: number | null,
  filterIds?: number[],
  showWon?: boolean,
): Promise<quotesGrid[]> => {
  const token = localStorage.getItem('authToken')
  console.log("showWon:", showWon)
  if (!token) {
    throw new Error('Authorization token not found')
  }

  var url = `https://${apiURL}/Quotes?ShowLost=${showRejected}&ShowWon=${showWon === undefined ? false : showWon}`;
  if (month != null && month > 0) {
    url = url + `&month=${month}`;
  }

  if (filterIds && filterIds.length > 0) {
    filterIds.forEach((id) => {
      url += `&CreatedBy=${id}`;
    });
  } 

  const response = await axios.get<quotesGrid[]>(
    url,
    {
      headers: {
        'Content-Type': 'application/json',
        Authorization: 'Bearer ' + token,
      },
    }
  )

  const quoteData: quotesGrid[] = response.data.map((item) => ({
    noteCount: item.noteCount,
    quoteId: item.quoteId,
    ref: item.ref,
    refFormatted: item.refFormatted,
    client: item.client,
    site: item.site,
    worktype: item.worktype,
    priority: item.priority,
    priorityId: item.priorityId,
    followupDate: dateFormatter.format(item.followupDate),
    createdDate: item.createdDate,
    wonDate: item.wonDate,
    lostDate: item.lostDate,
    description: item.description,
    cost: item.cost,
    createdBy: item.createdBy,
    assignedTo: item.assignedTo,
    quoteDoc: true,
    qualsDescription: item.qualsDescription,
    quoteTemplateId: item.quoteTemplateId,
    shortDescription: item.shortDescription,
    worksDescription: item.worksDescription,
    value: item.value,
    isSent: item.isSent,
    sentDate: item.sentDate,
    note1Details: item.note1Details,
    note2Details: item.note2Details,
    note3Details: item.note3Details,
    note1Completed: item.note1Completed,
    note2Completed: item.note2Completed,
    note3Completed: item.note3Completed,
  }))
  return quoteData
}

export const getQuotesWithPagination = async (params: {
  skip: number
  take: number
  showRejected: boolean
  showWon: boolean
  month: number | null
  filterIds: number[]
  isSent: boolean | null
  sort: any[]
  filter: any
}): Promise<{ data: quotesGrid[], total: number }> => {
  const token = localStorage.getItem('authToken')

  if (!token) {
    throw new Error('Authorization token not found')
  }

  // Build query string parameters
  const queryParams = new URLSearchParams({
    skip: params.skip.toString(),
    take: params.take.toString(),
    ShowLost: params.showRejected.toString(),
    ShowWon: params.showWon.toString(),
  })

  // Add month if specified
  if (params.month !== null && params.month > 0) {
    queryParams.append('month', params.month.toString())
  }

  // Add filter IDs if specified
  if (params.filterIds && params.filterIds.length > 0) {
    params.filterIds.forEach((id) => {
      queryParams.append('CreatedBy', id.toString())
    })
  }

  // Add sent status if specified (null means show all)
  if (params.isSent !== null) {
    queryParams.append('IsSent', params.isSent.toString())
  }

  // Add sorting parameters if present
  if (params.sort && params.sort.length > 0) {
    // Use only the first sort item (primary sort)
    const primarySort = params.sort[0]
    queryParams.append('sortField', primarySort.field)
    queryParams.append('sortDir', primarySort.dir)
  }

  // Add filter parameters if present
  if (params.filter) {
    queryParams.append('filter', JSON.stringify(params.filter))
  }

  const url = `https://${apiURL}/Quotes/paginated?${queryParams.toString()}`
  console.log('API URL:', url)
  console.log('Params:', { skip: params.skip, take: params.take })

  const response = await axios.get<{ data: quotesGrid[], total: number }>(
    url,
    {
      headers: {
        'Content-Type': 'application/json',
        Authorization: 'Bearer ' + token,
      },
    }
  )

  // Transform the data to match our interface
  const quoteData: quotesGrid[] = response.data.data.map((item) => ({
    ...item,
    followupDate: dateFormatter.format(item.followupDate),
    createdDate: item.createdDate,
    wonDate: item.wonDate,
    lostDate: item.lostDate,
  }))

  return {
    data: quoteData,
    total: response.data.total
  }
}

export const getClientsForQuotesFilterDropdown = async (searchTerm: string): Promise<dropDown[]> => {
  const token = localStorage.getItem('authToken')

  if (!token) {
    throw new Error('Authorization token not found')
  }

  const response = await axios.get(`https://${apiURL}/Quotes/Clients/ForFilterDropdown?filter=${encodeURIComponent(searchTerm)}`, {
    headers: {
      'Content-Type': 'application/json',
      Authorization: 'Bearer ' + token,
    },
  })

  return response.data.map((item: any) => ({
    text: item.text,
    value: item.value,
  }))
}

export const getSitesForQuotesFilterDropdown = async (searchTerm: string): Promise<dropDown[]> => {
  const token = localStorage.getItem('authToken')

  if (!token) {
    throw new Error('Authorization token not found')
  }

  const response = await axios.get(`https://${apiURL}/Quotes/Sites/ForFilterDropdown?filter=${encodeURIComponent(searchTerm)}`, {
    headers: {
      'Content-Type': 'application/json',
      Authorization: 'Bearer ' + token,
    },
  })

  return response.data.map((item: any) => ({
    text: item.text,
    value: item.value,
  }))
}

export const getWorkTypesForQuotesFilterDropdown = async (searchTerm: string): Promise<dropDown[]> => {
  const token = localStorage.getItem('authToken')

  if (!token) {
    throw new Error('Authorization token not found')
  }

  const response = await axios.get(`https://${apiURL}/Quotes/WorkType/ForFilterDropdown?filter=${encodeURIComponent(searchTerm)}`, {
    headers: {
      'Content-Type': 'application/json',
      Authorization: 'Bearer ' + token,
    },
  })

  return response.data.map((item: any) => ({
    text: item.text,
    value: item.value,
  }))
}

export const getAssignedToForQuotesFilterDropdown = async (searchTerm: string): Promise<dropDown[]> => {
  const token = localStorage.getItem('authToken')

  if (!token) {
    throw new Error('Authorization token not found')
  }

  const response = await axios.get(`https://${apiURL}/Quotes/AssignedTo/ForFilterDropdown?filter=${encodeURIComponent(searchTerm)}`, {
    headers: {
      'Content-Type': 'application/json',
      Authorization: 'Bearer ' + token,
    },
  })

  return response.data.map((item: any) => ({
    text: item.text,
    value: item.value,
  }))
}

export const getQuotesForQuotesFilterDropdown = async (searchTerm: string): Promise<dropDown[]> => {
  const token = localStorage.getItem('authToken')

  if (!token) {
    throw new Error('Authorization token not found')
  }

  const response = await axios.get(`https://${apiURL}/Quotes/ForFilterDropdown?filter=${encodeURIComponent(searchTerm)}`, {
    headers: {
      'Content-Type': 'application/json',
      Authorization: 'Bearer ' + token,
    },
  })

  const data = response.data

  const quoteDropdownData: dropDown[] = (data as dropDown[]).map((item: any) => ({
    text: item.text,
    value: item.value,
  }))

  return quoteDropdownData
}

export const submitQuote = async (quote: postQuoteTypes) => {
  const token = localStorage.getItem('authToken')

  if (!token) {
    throw new Error('Authorization token not found')
  }

  const response = await axios({
    url: `https://${apiURL}/Quotes`,
    method: 'post',
    data: {
      contactId: quote.contactId,
      clientId: quote.clientId,
      siteId: quote.siteId,
      priorityId: quote.priorityId,
      enquirysourceId: quote.enquirysourceId ? quote.enquirysourceId : 0,
      description: quote.description,
      worktypeId: quote.worktypeId,
      cost: quote.cost,
      followupDate: quote.followupDate,
      enquiryId: quote.enquiryId,
      employeeAssignedToId: quote.employeeAssignedToId,
      likelihoodId: quote.likelihoodId,
      requireFollowups: quote.requireFollowups ?? true,
    },
    headers: {
      'Content-Type': 'application/json',
      Authorization: 'Bearer ' + token,
    },
  })

  const data: postQuoteTypes = response.data
  return data
}

export const getQuoteDropdown = async (): Promise<dropDown[]> => {
  const token = localStorage.getItem('authToken')

  if (!token) {
    throw new Error('Authorization token not found')
  }

  const response = await axios.get(`https://${apiURL}/Quotes/ForDropdown`, {
    headers: {
      'Content-Type': 'application/json',
      Authorization: 'Bearer ' + token,
    },
  })

  const data = response.data

  const quoteDropdownData: dropDown[] = (data as dropDown[]).map(
    (item: any) => ({
      value: item.value,
      text: item.text,
    })
  )

  return quoteDropdownData
}

export const getTargetQuote = async (
  quoteId: number
): Promise<targetQuoteTypes> => {
  const token = localStorage.getItem('authToken')

  if (!token) {
    throw new Error('Authorization token not found')
  }

  const response = await axios.get(`https://${apiURL}/Quotes/${quoteId}`, {
    headers: {
      'Content-Type': 'application/json',
      Authorization: 'Bearer ' + token,
    },
  })

  const data: targetQuoteTypes = response.data

  console.log("quote information");
console.log(data);

  const targetQuoteData: targetQuoteTypes = {
    // Carried through explicitly: this builder reshapes the response field
    // by field, so anything not named here is dropped before it reaches the form.
    workingsAdjustmentPercent: data.workingsAdjustmentPercent ?? null,
    contact: {
      contactId: data.contact?.contactId || 0,
      title: data.contact?.title,
      firstname: data.contact?.firstname,
      lastname: data.contact?.lastname,
      phone: data.contact?.phone,
      email: data.contact?.email,
    },
    jobId: data.jobId,
    quoteUrl: data.quoteUrl,
    jobRef: data.jobRef,
    enquiryRef: data.enquiryRef,
    enquiry: data.enquiry,
    quoteId: data.quoteId,
    ref: data.ref,
    refFormatted: data.refFormatted,
    cost: data.cost,
    description: data.description,
    createdDate: dateFormatter.format(data.createdDate),
    followupDate: data.followupDate,
    wonDate: dateFormatter.format(data.wonDate),
    lostDate: data.lostDate,
    client: {
      clientId:
        data.client && data.client.clientId ? data.client.clientId : null,
      clientName:
        data.client && data.client.clientName
          ? data.client.clientName
          : 'No client name',
      accountRef:
        data.client && data.client.accountRef
          ? data.client.accountRef
          : 'No account ref',
      contactName:
        data.client && data.client.contactName
          ? data.client.contactName
          : 'No contact name',
      phone:
        data.client && data.client.phone
          ? data.client.phone
          : 'No phone number',
      email:
        data.client && data.client.email
          ? data.client.email
          : 'No email address',
    },
    priority: {
      priorityId:
        data.priority && data.priority.priorityId
          ? data.priority.priorityId
          : null,
      description:
        data.priority && data.priority.description
          ? data.priority.description
          : 'No priority set',
    },
    enquirysource: {
      enquirysourceId:
        data.enquirysource && data.enquirysource.enquirysourceId
          ? data.enquirysource.enquirysourceId
          : null,
      description:
        data.enquirysource && data.enquirysource.description
          ? data.enquirysource.description
          : 'No enquiry source set',
    },
    site: {
      siteId: data.site && data.site.siteId ? data.site.siteId : null,
      siteAddr:
        data.site && data.site.siteAddr
          ? data.site.siteAddr
          : 'No site address',
      postcode:
        data.site && data.site.postcode ? data.site.postcode : 'No postcode',
    },
    worktype: {
      worktypeId:
        data.worktype && data.worktype.worktypeId
          ? data.worktype.worktypeId
          : null,
      description:
        data.worktype && data.worktype.description
          ? data.worktype.description
          : 'No worktype set',
      group:
        data.worktype && data.worktype.group
          ? data.worktype.group
          : 'No worktype group set',
    },
    employeeCreatedby: {
      employeeId:
        data.employeeCreatedby && data.employeeCreatedby.employeeId
          ? data.employeeCreatedby.employeeId
          : null,
      employeeName:
        data.employeeCreatedby && data.employeeCreatedby.employeeName
          ? data.employeeCreatedby.employeeName
          : 'No employee name',
      position:
        data.employeeCreatedby && data.employeeCreatedby.position
          ? data.employeeCreatedby.position
          : 'No employee position',
    },
    employeeAssignedTo: {
      employeeId:
        data.employeeAssignedTo && data.employeeAssignedTo.employeeId
          ? data.employeeAssignedTo.employeeId
          : null,
      employeeName:
        data.employeeAssignedTo && data.employeeAssignedTo.employeeName
          ? data.employeeAssignedTo.employeeName
          : 'No employee name',
      position:
        data.employeeAssignedTo && data.employeeAssignedTo.position
          ? data.employeeAssignedTo.position
          : 'No employee position',
    },
    likelihood: {
      likelihoodId:
        data.likelihood && data.likelihood.likelihoodId
          ? data.likelihood.likelihoodId
          : null,
      description:
        data.likelihood && data.likelihood.description
          ? data.likelihood.description
          : 'No likelihood set',
    },
    enquiryId: data.enquiryId,
    qualsDescription: data.qualsDescription,
    quoteTemplateId: data.quoteTemplateId,
    shortDescription: data.shortDescription,
    value: data.value,
    worksDescription: data.worksDescription,
    isSent: data.isSent,
    sentDate: data.sentDate,
    projectFolderUrl: data.projectFolderUrl,
    requireFollowups: data.requireFollowups ?? true
  }

  return targetQuoteData
}

// The pickers hand their selection back as a string, and the API's id fields are
// ints - a quoted number is rejected outright, so ids are coerced on the way out.
// Required ids fall back to 0, which the API rejects with a validation message;
// optional ones go as null, which it reads as "nothing chosen".
const requiredId = (value: unknown): number => Number(value) || 0

const optionalId = (value: unknown): number | null => {
  if (value === null || value === undefined || value === '') {
    return null
  }

  const parsed = Number(value)
  return Number.isNaN(parsed) ? null : parsed
}

export const updateQuote = async (quote: updateQuoteTypes, quoteId: number) => {
  const token = localStorage.getItem('authToken')

  if (!token) {
    throw new Error('Authorization token not found')
  }

  const response = await axios({
    url: `https://${apiURL}/Quotes/${quoteId}`,
    method: 'put',
    data: {
      contactId: requiredId(quote.contactId),
      quoteUrl: quote.quoteUrl,
      quoteId: quoteId,
      clientId: requiredId(quote.clientId),
      siteId: requiredId(quote.siteId),
      priorityId: requiredId(quote.priorityId),
      enquirysourceId: requiredId(quote.enquirysourceId),
      description: quote.description,
      worktypeId: requiredId(quote.worktypeId),
      cost: quote.cost,
      followupDate: quote.followupDate,
      wonDate: quote.wonDate,
      lostDate: quote.lostDate,
      enquiryId: optionalId(quote.enquiryId),
      employeeAssignedToId: optionalId(quote.employeeAssignedToId),
      quoteTemplateId: optionalId(quote.quoteTemplateId),
      shortDescription:	quote.shortDescription,
      worksDescription:	quote.worksDescription,
      qualsDescription:	quote.qualsDescription,
      value: quote.value,
      isSent: quote.isSent,
      sentDate: quote.sentDate,
      projectFolderUrl: quote.projectFolderUrl,
      likelihoodId: optionalId(quote.likelihoodId),
      requireFollowups: quote.requireFollowups ?? true,
      workingsAdjustmentPercent: quote.workingsAdjustmentPercent ?? null,
    },
    headers: {
      'Content-Type': 'application/json',
      Authorization: 'Bearer ' + token,
    },
  })

  const data: updateQuoteTypes = response.data
  return data
}

export const getQuoteDocuments = async (quoteId: number): Promise<fileDocumentTypes[]> => {
  const token = localStorage.getItem('authToken')

  if (!token) {
    throw new Error('Authorization token not found')
  }

  const response = await axios.get(
    `https://${apiURL}/Quotes/${quoteId}/Documents`,
    {
      headers: {
        'Content-Type': 'application/json',
        Authorization: 'Bearer ' + token,
      },
    }
  )

  const data: fileDocumentTypes[] = response.data

  return data
}

export const quoteWon = async (quoteId: number, jobId: number) => {
  const token = localStorage.getItem('authToken')


  if (!token) {
    throw new Error('Authorization token not found')
  }

  const response = await axios({
    url: `https://${apiURL}/Quotes/MarkAsWon/${quoteId}/${jobId}`,
    method: 'put',
    headers: {
      'Content-Type': 'application/json',
      Authorization: 'Bearer ' + token,
    },
  })

  const data = response.data

  return data
}

export const quoteLost = async (quoteId: number, lostReasonId?: number) => {
  const token = localStorage.getItem('authToken')

  if (!token) {
    throw new Error('Authorization token not found')
  }

  const response = await axios({
    url: `https://${apiURL}/Quotes/MarkAsLost/${quoteId}${lostReasonId ? `?lostReasonId=${lostReasonId}` : ''}`,
    method: 'put',
    headers: {
      'Content-Type': 'application/json',
      Authorization: 'Bearer ' + token,
    },
  })

  const data = response.data

  return data
}

export interface QuoteTemplate {
  quoteTemplateId: number
  created: string
  createdBy: number
  modified: string
  modifiedBy: number
  name: string
  templateFile: QuoteTemplateFile
  shortDescription: string
  worksDescription: string
  totalCost: number
  qualificationsDescription: string
}

export interface QuoteTemplateFile {
  quoteTemplateFileId?: number
  fileId: number
  fileName: string
  fileUrl: string
}

/**
 * Retrieves the quote templates from the server.
 * @returns A promise that resolves to an array of QuoteTemplate objects.
 * @throws {Error} If the authorization token is not found.
 */
export const getQuoteTemplates = async (): Promise<QuoteTemplate[]> => {
  const token = localStorage.getItem('authToken');

  if (!token) {
     throw new Error('Authorization token not found');
  }

  const response = await axios.get(`https://${apiURL}/QuoteTemplate`, {
     headers: {
        'Content-Type': 'application/json',
        'Authorization': 'Bearer ' + token,
     }
  });

  const data: QuoteTemplate[] = response.data;
  return data;
};

/**
 * Retrieves a quote template by its ID.
 * @param id - The ID of the quote template to retrieve.
 * @returns A Promise that resolves to an array of QuoteTemplate objects.
 * @throws An error if the authorization token is not found.
 */
export const getQuoteTemplateById = async (id: number): Promise<QuoteTemplate> => {
  const token = localStorage.getItem('authToken');

  if (!token) {
     throw new Error('Authorization token not found');
  }

  const response = await axios.get(`https://${apiURL}/QuoteTemplate/${id}`, {
     headers: {
        'Content-Type': 'application/json',
        'Authorization': 'Bearer ' + token,
     }
  });

  const data: QuoteTemplate = response.data;
  return data;
};

export interface UpdateQuoteTemplate {
  quoteTemplateId: number
  name: string
  shortDescription: string
  worksDescription: string
  totalCost: number
  qualificationsDescription: string
  templateFile: QuoteTemplateFile
}

/**
 * Updates a quote template.
 * @param id - The ID of the quote template to update.
 * @param template - The updated quote template.
 * @returns A Promise that resolves to void.
 * @throws An error if the authorization token is not found.
 */
export const updateQuoteTemplate = async (id: number, template: UpdateQuoteTemplate): Promise<void> => {
  const token = localStorage.getItem('authToken');

  if (!token) {
     throw new Error('Authorization token not found');
  }

  const response = await axios.put(`https://${apiURL}/QuoteTemplate/${id}`, template, {
     headers: {
        'Content-Type': 'application/json',
        'Authorization': 'Bearer ' + token,
     }
  });

  console.log(response)
};

export interface CreateQuoteTemplate {
  name: string
  shortDescription: string
  worksDescription: string
  totalCost: number
  qualificationsDescription: string
  templateFile: QuoteTemplateFile
}

/**
 * Adds a new quote template.
 * @param template - The quote template to be added.
 * @returns A Promise that resolves to void.
 * @throws An error if the authorization token is not found.
 */
export const addNewQuoteTemplate = async (template: CreateQuoteTemplate): Promise<void> => {
  const token = localStorage.getItem('authToken');

  if (!token) {
     throw new Error('Authorization token not found');
  }

  const response = await axios.post(`https://${apiURL}/QuoteTemplate/`, template, {
     headers: {
        'Content-Type': 'application/json',
        'Authorization': 'Bearer ' + token,
     }
  });

  console.log(response)
};

export const getQuoteTemplateFiles = async (): Promise<QuoteTemplateFile[]> => {
  const token = localStorage.getItem('authToken');

  if (!token) {
     throw new Error('Authorization token not found');
  }

  const response = await axios.get(`https://${apiURL}/QuoteTemplateFile/`, {
     headers: {
        'Content-Type': 'application/json',
        'Authorization': 'Bearer ' + token,
     }
  });

return response.data;
};

/** A quote converted to a job - the job's own or one added to it since. */
export interface JobQuote {
  quoteId: number
  ref: number
  refFormatted: string
  description: string
  /** The quote's value. Its Cost column, not Value, like every quote figure in the portal. */
  cost: number
  /** When it was converted to the job. */
  wonDate: string | null
}

// Get quotes by job ID
export const getQuotesByJobId = async (jobId: number): Promise<JobQuote[]> => {
  const token = localStorage.getItem('authToken')

  if (!token) {
    throw new Error('Authorization token not found')
  }

  const response = await axios.get(`https://${apiURL}/Quotes/ByJob/${jobId}`, {
    headers: {
      'Content-Type': 'application/json',
      Authorization: 'Bearer ' + token,
    },
  })

  const data = response.data

  return data.map((quote: any) => ({
    quoteId: quote.quoteId,
    ref: quote.ref,
    refFormatted: quote.refFormatted,
    description: quote.description ?? '',
    cost: Number(quote.cost) || 0,
    wonDate: quote.wonDate ?? null,
  }));
};

// Get quotes by likelihood ID (for pipeline reporting), optionally narrowed to
// the quotes assigned to the given employees
export const getQuotesByLikelihood = async (likelihoodId: number, assignedToIds: number[] = []): Promise<targetQuoteTypes[]> => {
  const token = localStorage.getItem('authToken')

  if (!token) {
    throw new Error('Authorization token not found')
  }

  const query = new URLSearchParams({ likelihoodId: String(likelihoodId) })
  assignedToIds.forEach((id) => query.append('assignedTo', String(id)))

  const response = await axios.get(`https://${apiURL}/Quotes?${query}`, {
    headers: {
      'Content-Type': 'application/json',
      Authorization: 'Bearer ' + token,
    },
  })

  return response.data
}
// Increment follow-up count for a quote
export const incrementFollowUpCount = async (quoteId: number): Promise<void> => {
  const token = localStorage.getItem('authToken')

  if (!token) {
    throw new Error('Authorization token not found')
  }

  await axios.put(
    `https://${apiURL}/Quotes/IncrementFollowUpCount/${quoteId}`,
    {},
    {
      headers: {
        'Content-Type': 'application/json',
        Authorization: 'Bearer ' + token,
      },
    }
  )
}

/** A file on the quote, or its enquiry, that can go with the email. */
export interface QuoteEmailFile {
  documentId: number
  fileName: string
  /** Ticked to start with: the newest PDF, most likely the quote itself. */
  suggested: boolean
}

/** What emailing a quote would send, for the user to change before it goes. */
export interface QuoteEmailPreview {
  /** The contact's address, or null if they have none. */
  to: string | null
  contactName: string | null
  /** The mailbox it is sent from. */
  from: string
  /** Why it can't be sent, other than a missing address. */
  problem: string | null
  templates: { emailTemplateId: number; name: string; isDefault: boolean }[]
  emailTemplateId: number | null
  subject: string
  body: string
  files: QuoteEmailFile[]
  emailedDate: string | null
  emailedTo: string | null
  emailedCc: string | null
  emailedBy: string | null
}

export interface QuoteEmailSend {
  emailTemplateId: number | null
  subject: string
  body: string
  /** Addresses separated by commas, semicolons or spaces. */
  cc: string
  documentIds: number[]
}

export interface QuoteEmailResult {
  emailedDate: string
  emailedTo: string
  emailedCc: string | null
  emailedBy: string | null
}

const authHeaders = () => {
  const token = localStorage.getItem('authToken')

  if (!token) {
    throw new Error('Authorization token not found')
  }

  return { 'Content-Type': 'application/json', Authorization: 'Bearer ' + token }
}

/** The email filled from a template - the default unless one is given. */
export const getQuoteEmailPreview = async (quoteId: number, templateId?: number): Promise<QuoteEmailPreview> => {
  const response = await axios.get(`https://${apiURL}/Quotes/${quoteId}/Email`, {
    headers: authHeaders(),
    params: templateId ? { templateId } : undefined,
  })
  return response.data
}

/** Emails the quote from the shared mailbox and marks it sent. */
export const emailQuote = async (quoteId: number, email: QuoteEmailSend): Promise<QuoteEmailResult> => {
  const response = await axios.post(`https://${apiURL}/Quotes/${quoteId}/Email`, email, { headers: authHeaders() })
  return response.data
}
